﻿import { uiFormat, uiText } from '../../i18n';

export interface ResultLine {
  t: number;
  d: number;
  name: string;
  text: string;
  orb: string;
  voiceId?: number;
  /** Имя голоса на момент заказа (voice_name строки) */
  voiceName?: string;
}
/** Preserve server timing and speaker snapshots; never infer timings from text length. */
export function resultLines(value: unknown, depth = 0): ResultLine[] {
  if (!value || depth > 8) return [];
  if (Array.isArray(value)) return value.slice(0, 1000).flatMap(item => resultLines(item, depth + 1));
  if (typeof value !== 'object') return [];
  const row = value as Record<string, unknown>;
  if (Array.isArray(row.timeline) && Array.isArray(row.source_lines)) {
    const sources = new Map(
      row.source_lines
        .filter(item => item && typeof item === 'object')
        .map(item => {
          const source = item as Record<string, unknown>;
          return [source.line_key, source] as const;
        }),
    );
    return resultLines(
      row.timeline.map(item => {
        if (!item || typeof item !== 'object') return item;
        const line = item as Record<string, unknown>;
        return { ...sources.get(line.line_key), ...line };
      }),
      depth + 1,
    );
  }
  const text = typeof row.text === 'string' ? row.text : typeof row.content === 'string' ? row.content : null;
  const time = row.start_ms != null ? Number(row.start_ms) / 1000 : Number(row.start ?? row.start_s ?? row.t ?? 0);
  if (text != null && ['role', 'role_name', 'speaker', 'speaker_name', 'start', 'start_ms', 'start_s', 't'].some(key => row[key] != null)) {
    const role = row.role_name ?? row.role ?? row.speaker_name ?? row.speaker;
    const name =
      typeof role === 'string'
        ? role
        : typeof role === 'number'
          ? uiFormat('Собеседник {0}', role + 1)
          : role && typeof role === 'object'
            ? String((role as Record<string, unknown>).name ?? '')
            : '';
    const duration =
      row.duration_ms != null
        ? Number(row.duration_ms) / 1000
        : Number(
            row.duration ??
              row.duration_s ??
              row.d ??
              (row.end_ms != null ? Number(row.end_ms) / 1000 - time : row.end != null ? Number(row.end) - time : 0),
          );
    return [
      {
        t: Number.isFinite(time) ? Math.max(0, time) : 0,
        d: Number.isFinite(duration) ? Math.max(0, duration) : 0,
        name: [name, typeof row.voice_name === 'string' ? row.voice_name : ''].filter(Boolean).join(' · '),
        text,
        orb: '',
        ...(typeof row.voice_id === 'number' && Number.isSafeInteger(row.voice_id) && row.voice_id > 0 ? { voiceId: row.voice_id } : {}),
        ...(typeof row.voice_name === 'string' && row.voice_name ? { voiceName: row.voice_name } : {}),
      },
    ];
  }
  return ['timeline', 'lines', 'transcript', 'segments', 'utterances', 'data'].flatMap(key => resultLines(row[key], depth + 1));
}

export interface TextBlock {
  label: string;
  text: string;
  /** Описание товара (как textBlocks шаблона): h — заголовок крупно, ul — пункты, kw — фразы; без поля — абзацы */
  kind?: 'h' | 'ul' | 'kw' | 'seo';
  items?: string[];
}
const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : []);
/** Описание товара: заголовок, описание, преимущества, ключевые слова — четыре блока шаблона, а не блок на каждый пункт */
function seoBlocks(data: Record<string, unknown>): TextBlock[] | null {
  const benefits = strings(data.benefits),
    keywords = strings(data.keywords);
  if (!benefits.length && !keywords.length) return null;
  const title = typeof data.title === 'string' ? data.title : '',
    desc = typeof data.description === 'string' ? data.description : '';
  return [
    ...(title ? [{ label: uiText('Заголовок'), text: title, kind: 'h' as const }] : []),
    ...(desc ? [{ label: uiText('Описание'), text: desc, kind: 'seo' as const }] : []),
    ...(benefits.length ? [{ label: uiText('Преимущества'), text: benefits.map(b => '• ' + b).join('\n'), kind: 'ul' as const, items: benefits }] : []),
    ...(keywords.length ? [{ label: uiText('Ключевые слова'), text: keywords.join(', '), kind: 'kw' as const, items: keywords }] : []),
  ];
}
export function textBlocks(value: unknown, depth = 0): TextBlock[] {
  if (depth > 8 || value == null) return [];
  if (typeof value === 'string') return [{ label: '', text: value }];
  if (Array.isArray(value)) return value.slice(0, 100).flatMap(item => textBlocks(item, depth + 1));
  if (typeof value !== 'object') return [];
  const data = value as Record<string, unknown>;
  const seo = seoBlocks(data);
  if (seo) return seo;
  const labels: Record<string, string> = {
    text: 'Текст',
    title: 'Заголовок',
    body: 'Описание',
    description: 'Описание',
    lyrics: 'Текст песни',
    keywords: 'Ключевые слова',
    benefits: 'Преимущества',
    caption: 'Подпись',
    variants: 'Варианты',
    lines: 'Реплики',
    parts: 'Части',
    timeline: 'Реплики',
    transcript: 'Расшифровка',
    paragraphs: 'Абзацы',
    post_text: 'Текст поста',
    blocks: 'Текст',
    content: 'Текст',
    summary: 'Кратко',
  };
  return Object.entries(data)
    .filter(([key]) => key in labels)
    .flatMap(([key, item]) => textBlocks(item, depth + 1).map(block => ({ label: block.label || uiText(labels[key]), text: block.text })));
}

export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface VideoRow {
  title: string;
  /** Ролики ряда: номер в списке роликов результата и подпись сервера (caption · sub) */
  items: Array<{ video: number; cap: string; sub: string }>;
}
const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
/** Ряды роликов как на экране «Рилс с вами»: группы `result.data.groups` (03-api §9.4 `video_set`: элемент `{index, caption, sub}`;
    запасная форма сервера MessagingStudioScreenCatalog — `{asset_index, n, label}`), остальные ролики — одним рядом без шапки.
    videoIndices — индексы файлов-роликов в порядке списка роликов результата */
export function videoRows(data: unknown, videoIndices: number[]): VideoRow[] {
  const pos = new Map(videoIndices.map((index, i) => [index, i]));
  const groups = data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).groups) ? ((data as Record<string, unknown>).groups as unknown[]) : [];
  let next = 0;
  const out: VideoRow[] = groups
    .filter((g): g is Record<string, unknown> => !!g && typeof g === 'object')
    .map(group => ({
      title: str(group.title),
      items: (Array.isArray(group.items) ? group.items : [])
        .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
        .flatMap(item => {
          const index = Number.isInteger(item.index) ? (item.index as number) : Number.isInteger(item.asset_index) ? (item.asset_index as number) : next;
          next = index + 1;
          const video = pos.get(index);
          return video === undefined ? [] : [{ video, cap: str(item.caption) || str(item.label), sub: str(item.sub) }];
        }),
    }));
  const used = new Set(out.flatMap(r => r.items.map(x => x.video)));
  const rest = videoIndices.map((_, i) => i).filter(i => !used.has(i));
  if (rest.length) out.push({ title: '', items: rest.map(video => ({ video, cap: '', sub: '' })) });
  return out.filter(r => r.items.length);
}
